(ns practitest.frontend.components.project-assistant.events
  (:require
    [clojure.string :as str]
    [practitest.frontend.common-entities.events :as common-events]
    [practitest.frontend.common-entities.metadata :as md]
    [practitest.frontend.common.fetch :refer [fetch-json fetch-text]]
    [practitest.frontend.common.i18n :refer [get-translation format-translation]]
    [practitest.frontend.common.mixpanel :as mixpanel]
    [practitest.frontend.common.re-frame :as rf-common]
    [practitest.frontend.components.grid.server :as server]
    [practitest.frontend.components.notifications :as notifications]
    [practitest.frontend.components.project-assistant.actions :as actions]
    [practitest.frontend.components.project-assistant.schema :refer [event-interceptors]]
    [practitest.frontend.components.project-assistant.tools :as tools]
    [practitest.frontend.device-connection.stream :as stream]
    [practitest.frontend.routing.backend-routes :as broutes]
    [practitest.frontend.subs :as subs]
    [re-frame.core :as re-frame]
    [reagent.core :as reagent]))

;; ============================================================================
;; Project Assistant - events + effects
;; ============================================================================
;;
;; Streaming
;;   The assistant talks to the user via a streaming HTTP connection opened
;;   by `::connect!` (see `device-connection.stream` - fetch + ReadableStream).
;;   Incoming server messages arrive on that stream.
;;   Outgoing user messages POST via `::stream-send!`.
;;
;;   Server messages dispatched by `:type`:
;;     "token"      -> `::stream-token`       (append to current assistant message)
;;     "tool-calls" -> `::execute-tool-calls` (split into auto-execute vs confirm)
;;     "usage"      -> `::report-usage`       (post monthly usage)
;;     "title"      -> `::title-received`     (conversation auto-naming)
;;     "done"       -> `::stream-done`        (flush pending actions, persist tail)
;;     "error"      -> `::stream-error`
;;
;; Action framework (read vs write tools)
;;   Tool calls split on tools/metadata `:category`:
;;     `:read`        run inline, send result back via `::stream-send!`
;;                    (also produces an informational "tool-call" message)
;;     `:write/:soft` push a "pending-action" message into the chat,
;;                    confirm/dismiss handlers live in `actions.cljs`.
;;   Pending actions are buffered separately and flushed by `flush-pending-actions`
;;   on `::stream-done` so confirmation cards appear AFTER the assistant text.
;;
;; Message lifecycle
;;   sent -> persisted (server assigns `:id`) -> optionally patched (e.g. status).
;;   `::messages-persisted` attaches server `:id` to local messages by `:position`.
;;   `:next-position` increments per persisted-or-skipped message.
;;
;; Connection state
;;   `defonce` `conn-state` (atom) holds the streaming connection outside `app-db`
;;   because it's a mutable JS object.
;;
;; Where to look for what
;;   `schema.cljs`   re-frame state shape + validate-state interceptor
;;   `subs.cljs`     re-frame subscriptions
;;   `actions.cljs`  pending-action card renderers + executors (write tools)
;;   `tools.cljs`    LLM tool catalog + frontend-only metadata

(def ^:private max-inline-possible-values 20)

;; ---- helpers ----

(defn- build-fields-for-assistant [db]
  (->> (subs/fields-by-ptql-entity (:fields db) (:entity-fields db))
       (mapcat (fn [[entity-name fields]]
                 (map (fn [f]
                        (cond-> {:name (:name f)
                                 :field_format (or (:type f) "text")
                                 :entities [entity-name]}
                          (and (contains? #{"list" "multilist" "linkedlist"} (:type f))
                               (seq (:values f))
                               (<= (count (:values f)) max-inline-possible-values))
                          (assoc :possible_values (:values f))))
                      fields)))
       (group-by (juxt :name :field_format))
       (mapv (fn [[_ fs]]
               (-> (first fs)
                   (assoc :entities (vec (mapcat :entities fs))))))))

(defn- active-convo-storage-key [user-id project-id]
  (str "assistant:active:" user-id ":" project-id))

(defn- read-active-convo-id [user-id project-id]
  (when (and user-id project-id)
    (some-> (.-localStorage js/window)
            (.getItem (active-convo-storage-key user-id project-id))
            (js/parseInt)
            (as-> n (when-not (js/isNaN n) n)))))

(defn- execute-get-field-values [fields-by-entity users entity-module field-name]
  (let [entity-name (some-> entity-module (md/entity-by-module) (subs/entity-kw->ptql-name))
        match-in (fn [fields]
                   (some #(when (or (= (:name %) field-name) (= (:id %) field-name)) %) fields))
        matches (if entity-name
                  (some-> (match-in (get fields-by-entity entity-name)) (vector))
                  ;; entity_type missing or unknown (e.g. replay of an old conversation)
                  ;; search all entities, but refuse to guess on a cross-entity collision
                  (->> (vals fields-by-entity)
                       (keep match-in)
                       (group-by :id)
                       (vals)
                       (map first)))
        found (first matches)]
    (js/Promise.resolve
      (js/JSON.stringify
        (clj->js
          (cond
            (> (count matches) 1)
            {:error (str "Field name '" field-name "' exists on multiple entities with different meanings. "
                         "Call again with entity_type to disambiguate.")}

            (nil? found)
            {:error (str "Unknown field: " field-name (when entity-name (str " on " entity-name)))}

            (= "user" (:type found))
            {:field_name (:name found)
             :values (->> users
                          (filter #(= "user" (:type %)))
                          (keep :email)
                          (vec))
             :note "Values are user emails. CURRENT_USER is also valid for filtering by the current session user."}

            (seq (:values found))
            {:field_name (:name found)
             :values (:values found)}

            :else
            {:field_name (:name found)
             :values []
             :note "This field has no enumerable values."}))))))

(def ^:private ptql-result-rows 10)
(def ^:private ptql-result-trim-fields [:value_score_data])

;; list2 will return complete resultset if it's 500 entities or fewer,
;; regardless of page size (for client-side pagination)
;; it's a waste of tokens, so we trim it manually
;; there are also some fields that aren't useful and large (like value_score_data) which we will also trim
(defn- trim-ptql-result [body]
  (-> body
      (select-keys [:total_count :data])
      (update :data #(->> %
                          (take ptql-result-rows)
                          (mapv (fn [row]
                                  (apply dissoc row ptql-result-trim-fields)))))))

(defn- execute-ptql-query [project-id entity-module query]
  (let [entity (md/entity-by-module entity-module)
        route-name (get-in md/entities-metadata [entity :urls :fetch-list])]
    (when-not route-name (throw (js/Error. (str "Unknown entity type: " entity-module))))
    (-> (fetch-json (broutes/get-url route-name {:p_id project-id})
                    {:method "POST" :body {:ptql query :page 1 :per_page ptql-result-rows}})
        (.then (fn [{:keys [ok? status body]}]
                 (if ok?
                   (js/JSON.stringify (clj->js (trim-ptql-result body)))
                   (str "Error " status ": " (pr-str body))))))))

(defn- execute-find-matching-views [project-id entity-type query]
  (-> (fetch-text (broutes/get-url "custom_views#find_matching" {:p_id project-id})
                  {:method "POST" :body {:entity_type entity-type :query query}})
      (.then (fn [{:keys [ok? status body]}]
               (if ok?
                 body
                 (str "Error " status ": " body))))))

(defn- flush-pending-actions
  "Moves buffered pending-action messages into the main :messages list.
  Called from `::stream-done` / `::stream-error` so confirmation cards always appear AFTER the assistant's response."
  [db]
  (let [buffer (get-in db [:project-assistant :pending-actions-buffer])]
    (if (seq buffer)
      (-> db
          (update-in [:project-assistant :messages] (fnil into []) buffer)
          (update-in [:project-assistant] dissoc :pending-actions-buffer))
      db)))

(defn- message->server
  "Converts a frontend message map to the server shape:
  top-level keys stay, `:content`/etc go into `:data` (snake_cased)."
  [{:keys [role position content context-entity tool-call-id tool-name args status result] :as _msg}]
  {:position position
   :role role
   :data (cond-> {}
           content (assoc :content content)
           context-entity (assoc :context_entity context-entity)
           tool-call-id (assoc :tool_call_id tool-call-id)
           tool-name (assoc :tool_name tool-name)
           args (assoc :args args)
           status (assoc :status (name status))
           result (assoc :result result))})

(defn- server->message
  "Converts a server message map (snake_case data) to the frontend shape."
  [{:keys [id position role data] :or {data {}}}]
  (cond-> {:role role :position position :id id}
    (:content data) (assoc :content (:content data))
    (:context_entity data) (assoc :context-entity (:context_entity data))
    (:tool_call_id data) (assoc :tool-call-id (:tool_call_id data))
    (:tool_name data) (assoc :tool-name (:tool_name data))
    (:args data) (assoc :args (:args data))
    (:status data) (assoc :status (keyword (:status data)))
    (:result data) (assoc :result (:result data))))

(defn- pending-action-msg [{:keys [id name args]}]
  {:role "pending-action"
   :tool-call-id id
   :tool-name name
   :args args
   :status :pending
   :result nil})

(defn- network-error?
  "A failed `js/fetch` rejects with a `TypeError` only on network-layer failure.
  HTTP error statuses resolve with `ok=false` instead of rejecting."
  [err]
  (instance? js/TypeError err))

(defn- network-error-message []
  (get-translation "assistant.error.network"
                   "Unable to complete the request. Please check your internet connection and try again."))

(def ^:private handshake-timeout-ms 10000)
(def ^:private turn-idle-timeout-ms 45000)
(def ^:private liveness-timeout-ms 75000) ;; 2.5x server heartbeats

(defn- connect-timeout-message [assistant-url]
  (let [host (try (.-host (js/URL. assistant-url)) (catch :default _ assistant-url))]
    (format-translation
      (get-translation "assistant.error.connect-timeout"
                       "Could not establish a streaming connection to the assistant. If you are on a corporate network, ask your IT team to allow streaming responses from %{host}. Close and reopen the assistant panel to retry.")
      :host host)))

(defn- build-llm-messages [display-messages]
  (let [with-context (fn [{:keys [role content context-entity] :as m}]
                       (if (and (= role "user") context-entity)
                         (assoc m :content (str "[Context: currently viewing " context-entity "]\n" content))
                         m))]
    (->> display-messages
         (filter #(contains? #{"user" "assistant"} (:role %)))
         (remove #(str/blank? (:content %)))
         (map with-context)
         (vec))))

(defn- stream-timeout-message []
  (get-translation "assistant.error.stream-timeout"
                   "The assistant stopped responding. Your message was saved. Please try again."))

(defn- drop-connection-fx [db]
  (cond-> {::disconnect! nil
           :dispatch-n [[::disconnected]]}
    (get-in db [:project-assistant :streaming?])
    (-> (assoc :db (assoc-in db [:project-assistant :retriable?] true))
        (update :dispatch-n #(into [[::stream-error (stream-timeout-message)]] %)))))

;; ---- state (outside of app-db) ----

(defonce conn-state (atom nil))
(defonce last-received (atom 0))

;; ---- effects ----

(re-frame/reg-fx
  ::connect!
  (fn [{:keys [url token]}]
    (let [conn (stream/connect!
                 {:base-url url
                  :headers {"Authorization" (str "Bearer " token)}
                  :on-open #(re-frame/dispatch [::connected])
                  :on-message (fn [msg]
                                (reset! last-received (.now js/Date))
                                (case (:type msg)
                                  ;; NOTE: "start" is ignored now,
                                  ;;       ::stream-token creates assistant message on first token, no need to setup
                                  "start" nil
                                  "token" (re-frame/dispatch [::stream-token (:content msg)])
                                  "tool-calls" (re-frame/dispatch [::execute-tool-calls (:calls msg)])
                                  "usage" (re-frame/dispatch [::report-usage msg])
                                  "done" (re-frame/dispatch [::stream-done])
                                  "error" (re-frame/dispatch [::stream-error (:message msg)])
                                  "title" (re-frame/dispatch [::title-received msg])
                                  nil))
                  :on-close (fn [_]
                              (re-frame/dispatch [::disconnected]))
                  :on-error (fn [err]
                              (re-frame/dispatch [::disconnected])
                              (re-frame/dispatch [::connection-error (str err)]))})]
      (reset! conn-state conn))))

(re-frame/reg-fx
  ::disconnect!
  (fn [_]
    (when-let [conn @conn-state]
      (stream/disconnect! conn)
      (reset! conn-state nil))))

(re-frame/reg-fx
  ::stream-send!
  (fn [{:keys [message]}]
    (if-let [conn @conn-state]
      (-> (stream/send! conn message)
          (.catch (fn [_err]
                    (re-frame/dispatch [::stream-error (network-error-message)]))))
      (re-frame/dispatch [::stream-error (network-error-message)]))))

(re-frame/reg-fx
  ::execute-tools!
  (fn [{:keys [calls project-id fields-by-entity users extra-results]}]
    (let [conn @conn-state
          promises (into-array
                     (map (fn [{:keys [id name args]}]
                            (-> (case name
                                  "get_field_values" (execute-get-field-values fields-by-entity
                                                                               users
                                                                               (:entity_type args)
                                                                               (:field_name args))
                                  "ptql_query" (execute-ptql-query project-id
                                                                   (:entity_type args)
                                                                   (:query args))
                                  "find_matching_views" (execute-find-matching-views project-id
                                                                                     (:entity_type args)
                                                                                     (:query args))
                                  (js/Promise.resolve (str "Unknown tool: " name)))
                                (.then (fn [result]
                                         {:tool-call-id id :result result}))
                                (.catch (fn [err]
                                          {:tool-call-id id :result (str "Error: " err)}))))
                          calls))]
      (-> (js/Promise.all promises)
          (.then (fn [results]
                   ;; the "turn" belongs to `conn`, if it was dropped or replaced, the turn is dead
                   (when (and conn (identical? conn @conn-state))
                     (re-frame/dispatch [::touch-activity])
                     (stream/send! conn
                                   {:type "tool-results"
                                    :results (into (vec results) (or extra-results []))}))))
          (.catch (fn [err]
                    (re-frame/dispatch [::stream-error
                                        (if (network-error? err)
                                          (network-error-message)
                                          (str "Tool execution failed: " err))])))))))

(re-frame/reg-fx
  ::set-active-conversation!
  (fn [{:keys [user-id project-id convo-id]}]
    (when (and user-id project-id)
      (let [storage (.-localStorage js/window)
            key (active-convo-storage-key user-id project-id)]
        (if convo-id
          (.setItem storage key (str convo-id))
          (.removeItem storage key))))))

(re-frame/reg-fx
  ::persist-messages!
  (fn [{:keys [project-id conversation-id messages on-success]}]
    (-> (fetch-json (broutes/get-url "assistant_messages#create"
                                     {:p_id project-id
                                      :assistant_conversation_id conversation-id})
                    {:method "POST" :body {:messages messages}})
        (.then (fn [{:keys [status ok? body]}]
                 (cond
                   (= 409 status) (re-frame/dispatch [::position-conflict])
                   ok? (re-frame/dispatch (conj on-success body))
                   :else (js/console.warn "Failed to persist messages:" status))))
        (.catch #(js/console.warn "Persistence error:" %)))))

(re-frame/reg-fx
  ::load-conversation!
  (fn [{:keys [project-id conversation-id on-success on-not-found]}]
    (-> (fetch-json (broutes/get-url "assistant_conversations#show" {:p_id project-id :id conversation-id}) {})
        (.then (fn [{:keys [status ok? body]}]
                 (cond
                   ok? (re-frame/dispatch (conj on-success body))
                   (= 404 status) (when on-not-found (re-frame/dispatch on-not-found))
                   :else (js/console.warn "Failed to load conversation:" status))))
        (.catch #(js/console.warn "Load conversation error:" %)))))

(re-frame/reg-fx
  ::check-budget!
  (fn [{:keys [project-id text]}]
    (-> (fetch-json (broutes/get-url "assistant#budget" {:p_id project-id}) {})
        (.then (fn [{:keys [body]}]
                 (re-frame/dispatch [::budget-checked body text])))
        (.catch (fn [_err]
                  (re-frame/dispatch [::budget-checked {:allowed true :status "ok"} text]))))))

;; ---- events ----

;; -- panel state --

(re-frame/reg-event-fx
  ::toggle-panel
  event-interceptors
  (fn [{db :db} _]
    (let [opening? (not (get-in db [:project-assistant :panel-open?]))
          connected? (get-in db [:project-assistant :connected?])
          project-id (subs/get-current-project-id db)
          user-id (:id (subs/get-current-user db))
          has-convo? (some? (get-in db [:project-assistant :conversation]))
          active-id (when (and opening? (not has-convo?))
                      (read-active-convo-id user-id project-id))]
      (when opening? (mixpanel/track "Assistant Opened"))
      (cond-> {:db (update-in db [:project-assistant :panel-open?] not)}
        (and opening? (not connected?))
        (assoc ::rf-common/http
               {:url (broutes/get-url "service_tokens#create" {:p_id project-id})
                :method "POST"
                :on-success [::token-received]
                :on-failure [::connection-error]})

        active-id
        (assoc :dispatch [::load-conversation active-id])))))

(re-frame/reg-event-db
  ::close-panel
  event-interceptors
  (fn [db _]
    (assoc-in db [:project-assistant :panel-open?] false)))

;; -- connection lifecycle --

(re-frame/reg-event-fx
  ::token-received
  event-interceptors
  (fn [{db :db} [_ {:keys [token]}]]
    {:db (assoc-in db [:project-assistant :token] token)
     ::connect! {:url (get-in db [:options :project-assistant-url]) :token token}
     :dispatch-later [{:ms handshake-timeout-ms :dispatch [::handshake-timeout token]}]}))

(re-frame/reg-event-fx
  ::handshake-timeout
  event-interceptors
  (fn [{db :db} [_ token]]
    (if (or (not= token (get-in db [:project-assistant :token]))
            (get-in db [:project-assistant :connected?])
            (not (get-in db [:project-assistant :panel-open?])))
      {}
      (do
        (mixpanel/track "Assistant Connect Timeout")
        {:db (-> db
                 (assoc-in [:project-assistant :connected?] false)
                 (assoc-in [:project-assistant :error]
                           (connect-timeout-message
                             (get-in db [:options :project-assistant-url]))))
         ::disconnect! nil}))))

(re-frame/reg-event-fx
  ::connected
  event-interceptors
  (fn [{db :db} _]
    {:db (cond-> (assoc-in db [:project-assistant :connected?] true)
           (not (get-in db [:project-assistant :retriable?]))
           (assoc-in [:project-assistant :error] nil))
     ::stream-send! {:message {:type "init"
                               :project (select-keys (:current-project db) [:id :name])
                               :fields (build-fields-for-assistant db)
                               :tools tools/catalog}}
     :dispatch-later [{:ms 30000 :dispatch [::liveness-check (get-in db [:project-assistant :token])]}]}))

(re-frame/reg-event-fx
  ::disconnected
  event-interceptors
  (fn [{db :db} _]
    (let [panel-open? (get-in db [:project-assistant :panel-open?])
          project-id (subs/get-current-project-id db)]
      (reset! conn-state nil)
      (cond-> {:db (-> db
                       (assoc-in [:project-assistant :connected?] false)
                       (assoc-in [:project-assistant :streaming?] false))}
        panel-open?
        (assoc ::rf-common/http
               {:url (broutes/get-url "service_tokens#create" {:p_id project-id})
                :method "POST"
                :on-success [::token-received]
                :on-failure [::connection-error]})))))

(re-frame/reg-event-fx
  ::connection-error
  event-interceptors
  (fn [{db :db} [_ resp]]
    {:db (-> db
             (assoc-in [:project-assistant :connected?] false)
             (assoc-in [:project-assistant :error] (or (:error resp) (str resp))))}))

(re-frame/reg-event-fx
  ::liveness-check
  event-interceptors
  (fn [{db :db} [_ token]]
    (cond
      (or (not= token (get-in db [:project-assistant :token]))
          (not (get-in db [:project-assistant :connected?])))
      {}

      (> (- (.now js/Date) @last-received) liveness-timeout-ms)
      (do
        (mixpanel/track "Assistant Stream Reconnect")
        (drop-connection-fx db))

      :else
      {:dispatch-later [{:ms 30000 :dispatch [::liveness-check token]}]})))

;; -- send message + streaming --

(re-frame/reg-event-fx
  ::send-message
  event-interceptors
  (fn [{db :db} [_ text]]
    (when (get-in db [:project-assistant :connected?])
      (let [project-id (subs/get-current-project-id db)]
        {:db (assoc-in db [:project-assistant :streaming?] true)
         ::check-budget! {:project-id project-id :text text}}))))

;; The actual send path. `::send-message` gates here via `::check-budget!`,
;; so we never enqueue a user message we can't afford.
;; If allowed:
;;   - first message in a conversation: create it via `::rf-common/http`,
;;     then `::conversation-created-start-stream` takes over to send + persist.
;;   - subsequent: send the chat over the stream and persist the user message
;;     (server returns `:id` which `::messages-persisted` attaches by `:position`).
;; If denied: show the "hard-limit" message, nothing is sent.
(re-frame/reg-event-fx
  ::budget-checked
  event-interceptors
  (fn [{db :db} [_ {:keys [allowed status used_cents budget_cents]} text]]
    (let [db (assoc-in db [:project-assistant :budget]
                       {:status status :used_cents used_cents :budget_cents budget_cents})]
      (if allowed
        (let [current-entity (:current-entity db)
              module (when current-entity
                       (get-in md/entities-metadata [current-entity :module]))
              position (get-in db [:project-assistant :next-position] 0)
              user-msg {:role "user" :content text :context-entity module :position position}
              display-messages (-> (get-in db [:project-assistant :messages] [])
                                   (conj user-msg))
              llm-messages (build-llm-messages display-messages)
              project-id (subs/get-current-project-id db)
              convo-id (get-in db [:project-assistant :conversation :id])
              model (get-in db [:project-assistant :conversation :model] "gpt5.4")]
          (mixpanel/track "Assistant Message Sent")
          (let [base-db (-> db
                            (assoc-in [:project-assistant :messages] display-messages)
                            (assoc-in [:project-assistant :streaming?] true)
                            (assoc-in [:project-assistant :error] nil)
                            (assoc-in [:project-assistant :retriable?] false)
                            (assoc-in [:project-assistant :last-activity] (.now js/Date))
                            (update-in [:project-assistant :next-position] (fnil inc 0)))]
            (assoc
              (if convo-id
                {:db base-db
                 ::stream-send! {:message {:type "chat"
                                           :messages llm-messages
                                           :conversation-id convo-id}}
                 ::persist-messages! {:project-id project-id
                                      :conversation-id convo-id
                                      :messages [(message->server user-msg)]
                                      :on-success [::messages-persisted]}}
                {:db base-db
                 ::rf-common/http {:url (broutes/get-url "assistant_conversations#create" {:p_id project-id})
                                   :method "POST"
                                   :body {:model model}
                                   :on-success [::conversation-created-start-stream user-msg llm-messages]}})
              :dispatch-later [{:ms turn-idle-timeout-ms :dispatch [::turn-watchdog]}])))
        (do
          (mixpanel/track "Assistant Budget Limit" {"status" "hard_limit"})
          {:db (-> db
                   (assoc-in [:project-assistant :streaming?] false)
                   (assoc-in [:project-assistant :error]
                             (get-translation "assistant.budget.hard-limit"
                                              "Monthly usage limit reached. Contact your account admin.")))})))))

(re-frame/reg-event-db
  ::touch-activity
  event-interceptors
  (fn [db _]
    (assoc-in db [:project-assistant :last-activity] (.now js/Date))))

(re-frame/reg-event-db
  ::stream-token
  event-interceptors
  (fn [db [_ content]]
    (let [messages (get-in db [:project-assistant :messages])
          last-msg (last messages)
          db (if (= "assistant" (:role last-msg))
               (update-in db [:project-assistant :messages (dec (count messages)) :content] str content)
               (update-in db [:project-assistant :messages] (fnil conj [])
                          {:role "assistant" :content content}))]
      (-> db
          (assoc-in [:project-assistant :last-activity] (.now js/Date))
          (assoc-in [:project-assistant :error] nil)
          (assoc-in [:project-assistant :retriable?] false)))))

;; End-of-stream: drain pending-actions buffer,
;; then persist the tail of messages produced during the stream (assistant text + tool-call info
;; messages - user messages were persisted when sent).
;; Position is assigned in order starting at `:next-position` so the server can detect concurrent
;; edits via 409. 
;; `::messages-persisted` attaches the returned `:id` to each.
(re-frame/reg-event-fx
  ::stream-done
  event-interceptors
  (fn [{db :db} _]
    (let [db (-> db
                 (assoc-in [:project-assistant :streaming?] false)
                 (flush-pending-actions))
          all-msgs (get-in db [:project-assistant :messages])
          next-pos (get-in db [:project-assistant :next-position] 0)
          tail (->> all-msgs
                    (drop-while #(some? (:id %))) ;; drop already persisted
                    (remove #(= "user" (:role %))) ;; drop user messages (persistent on send)
                    (map-indexed (fn [i msg]
                                   (assoc msg :position (+ next-pos i))))
                    (vec))
          project-id (subs/get-current-project-id db)
          convo-id (get-in db [:project-assistant :conversation :id])
          db (update-in db [:project-assistant :messages]
                        (fn [msgs]
                          (-> (into [] (drop-last (count tail) msgs))
                              (into tail))))]
      (cond-> {:db (-> db (update-in [:project-assistant :next-position] (fnil + 0) (count tail)))}
        (and convo-id (seq tail))
        (assoc ::persist-messages! {:project-id project-id
                                    :conversation-id convo-id
                                    :messages (mapv message->server tail)
                                    :on-success [::messages-persisted]})))))

(re-frame/reg-event-db
  ::stream-error
  event-interceptors
  (fn [db [_ message]]
    (-> db
        (assoc-in [:project-assistant :streaming?] false)
        (assoc-in [:project-assistant :error] (or message "Error from assistant"))
        (flush-pending-actions))))

(re-frame/reg-event-fx
  ::turn-watchdog
  event-interceptors
  (fn [{db :db} _]
    (let [last-activity (get-in db [:project-assistant :last-activity] 0)
          idle (- (.now js/Date) last-activity)]
      (cond
        (not (get-in db [:project-assistant :streaming?]))
        {}

        (< idle turn-idle-timeout-ms)
        {:dispatch-later [{:ms turn-idle-timeout-ms :dispatch [::turn-watchdog]}]}

        :else
        (do
          (mixpanel/track "Assistant Turn Timeout")
          (drop-connection-fx db))))))

(re-frame/reg-event-fx
  ::retry-turn
  event-interceptors
  (fn [{db :db} _]
    (when (get-in db [:project-assistant :connected?])
      (let [convo-id (get-in db [:project-assistant :conversation :id])]
        {:db (-> db
                 (assoc-in [:project-assistant :streaming?] true)
                 (assoc-in [:project-assistant :error] nil)
                 (assoc-in [:project-assistant :retriable?] false)
                 (assoc-in [:project-assistant :last-activity] (.now js/Date)))
         ::stream-send! {:message {:type "chat"
                                   :messages (build-llm-messages
                                               (get-in db [:project-assistant :messages]))
                                   :conversation-id convo-id}}
         :dispatch-later [{:ms turn-idle-timeout-ms :dispatch [::turn-watchdog]}]}))))

(re-frame/reg-event-fx
  ::title-received
  event-interceptors
  (fn [{db :db} [_ {:keys [conversation-id title]}]]
    (let [project-id (subs/get-current-project-id db)
          current-id (get-in db [:project-assistant :conversation :id])
          current-title (get-in db [:project-assistant :conversation :title])]
      (if (and (= conversation-id current-id)
               (str/blank? current-title))
        {:db (assoc-in db [:project-assistant :conversation :title] title)
         ::rf-common/http {:url (broutes/get-url "assistant_conversations#update"
                                                 {:p_id project-id
                                                  :id conversation-id})
                           :method "PATCH"
                           :body {:title title}}}
        ;; NOTE: stale - user switched conversations or already renamed, ignore.
        {}))))

;; Server returns persisted records with `:id`.
;; Match each back to the local message by `:position` and patch the `:id` in place.
;; Without this, confirm/dismiss actions (see `actions.cljs`) wouldn't have an `:id` to patch.
(re-frame/reg-event-db
  ::messages-persisted
  event-interceptors
  (fn [db [_ persisted-msgs]]
    (update-in db [:project-assistant :messages]
               (fn [msgs]
                 (mapv (fn [msg]
                         (if-let [persisted (some #(when (= (:position %) (:position msg)) %) persisted-msgs)]
                           (assoc msg :id (:id persisted))
                           msg))
                       msgs)))))

(re-frame/reg-event-db
  ::position-conflict
  event-interceptors
  (fn [db _]
    ;; TODO: refactor it later, right now just show simple banner
    (assoc-in db [:project-assistant :error]
              (get-translation "assistant.position-conflict"
                               "This conversation was modified in another tab. Refresh to continue."))))

;; -- tool execution --

(def ^:private pending-action-placeholder
  "Tool result sent to the model for a write tool while the user has not confirmed it yet."
  (str "A card with a button for this action is shown under your reply. Nothing has happened yet; "
       "the user may click it or dismiss it. Point the user to the button below. "
       "Never say you opened, navigated to, created or saved anything."))

;; Tool calls split on tools/metadata `:category`:
;;   `:read`  - run inline via `::execute-tools!`, each read also adds an
;;              informational "tool-call" message to the chat
;;              ("Searching tests: `Status = \"Open\"`").
;;   `:write` - buffered as "pending-action" messages, user confirms via
;;              cards rendered in `actions.cljs`. A placeholder tool-result
;;              is still sent to the LLM right away (see `pending-action-placeholder`) so it can continue.
;; The pending buffer flushes on `::stream-done` so confirmation cards appear
;; AFTER the assistant's text response.
;; 
;; The placeholder + buffered-card dance is a workaround: tool-calling APIs
;; expect an immediate tool_result for every tool_call. We stretch tools to
;; cover both auto lookups AND user-confirmed actions.
;; The protocol has no native "wait for user approval" step.
(re-frame/reg-event-fx
  ::execute-tool-calls
  event-interceptors
  (fn [{db :db} [_ calls]]
    (doseq [{:keys [name]} calls]
      (mixpanel/track "Assistant Tool Used" {"tool" name}))
    (let [read? (fn [{:keys [name]}]
                  (= :read (get-in tools/metadata [name :category] :read)))
          read-calls (filter read? calls)
          pending-calls (remove read? calls)
          ;; informational messages for read actions
          db (reduce (fn [db call]
                       (update-in db [:project-assistant :messages] (fnil conj [])
                                  {:role "tool-call"
                                   :content (actions/render-tool-call-message call)}))
                     db read-calls)
          ;; pending-action messages are buffered; flushed on stream-done so they appear AFTER the assistant's response
          db (reduce (fn [db call]
                       (update-in db [:project-assistant :pending-actions-buffer] (fnil conj [])
                                  (pending-action-msg call)))
                     db pending-calls)
          pending-results (mapv (fn [{:keys [id]}]
                                  {:tool-call-id id
                                   :result pending-action-placeholder})
                                pending-calls)
          db (assoc-in db [:project-assistant :last-activity] (.now js/Date))]
      (if (seq read-calls)
        {:db db
         ::execute-tools! {:calls read-calls
                           :project-id (subs/get-current-project-id db)
                           :fields-by-entity (subs/fields-by-ptql-entity (:fields db) (:entity-fields db))
                           :users (:users db)
                           :extra-results pending-results}}
        {:db db
         ::stream-send! {:message {:type "tool-results" :results pending-results}}}))))

;; NOTE: this is for debugging "action framework" in REPL
(re-frame/reg-event-db
  ::dev-add-pending-action
  event-interceptors
  (fn [db [_ tool-name args]]
    (update-in db [:project-assistant :messages] (fnil conj [])
               (pending-action-msg {:id (str "dev-" (.getTime (js/Date.)))
                                    :name tool-name
                                    :args args}))))

;; -- usage tracking --

(re-frame/reg-event-fx
  ::report-usage
  event-interceptors
  (fn [{db :db} [_ {:keys [prompt_tokens completion_tokens model byok]}]]
    (let [project-id (subs/get-current-project-id db)]
      {::rf-common/http {:url (broutes/get-url "assistant#usage" {:p_id project-id})
                         :method "POST"
                         :body {:prompt_tokens prompt_tokens
                                :completion_tokens completion_tokens
                                :model model
                                :byok byok}}})))

;; -- ptql / view activation --

(re-frame/reg-event-fx
  ::activate-ptql
  event-interceptors
  (fn [{db :db} [_ entity ptql]]
    (let [state-key (get subs/index-grid-keys entity)
          new-db (assoc-in db [:project-assistant :active-ptql] {:entity entity :query ptql})]
      (if (common-events/on-index? db entity)
        {:db new-db
         ::common-events/fire-select-filter! "_DEFAULT_"
         :dispatch [::common-events/fetch-entity-list entity state-key server/default-query]}
        {:db new-db
         :dispatch (common-events/push-to-index db entity)}))))

(re-frame/reg-event-fx
  ::activate-view
  event-interceptors
  (fn [{db :db} [_ entity view-id]]
    {:db (update db :project-assistant dissoc :active-ptql)
     :dispatch [::common-events/activate-view entity view-id]}))

(re-frame/reg-event-db
  ::clear-active-ptql
  event-interceptors
  (fn [db _]
    (update db :project-assistant dissoc :active-ptql)))

;; -- conversation lifecycle --

(re-frame/reg-event-fx
  ::new-conversation
  event-interceptors
  (fn [{db :db} _]
    (when-not (get-in db [:project-assistant :streaming?])
      (let [user-id (:id (subs/get-current-user db))
            project-id (subs/get-current-project-id db)]
        {:db (update db :project-assistant merge
                     {:messages []
                      :pending-actions-buffer []
                      :streaming? false
                      :error nil
                      :retriable? false
                      :conversation nil
                      :next-position 0
                      :active-tab :chat})
         ::set-active-conversation! {:user-id user-id :project-id project-id :convo-id nil}}))))

(re-frame/reg-event-fx
  ::conversation-created-start-stream
  event-interceptors
  (fn [{db :db} [_ user-msg llm-messages conversation]]
    (let [project-id (subs/get-current-project-id db)
          user-id (:id (subs/get-current-user db))]
      {:db (assoc-in db [:project-assistant :conversation] conversation)
       ::stream-send! {:message {:type "chat"
                                 :messages llm-messages
                                 :conversation-id (:id conversation)}}
       ::persist-messages! {:project-id project-id
                            :conversation-id (:id conversation)
                            :messages [(message->server user-msg)]
                            :on-success [::messages-persisted]}
       ::set-active-conversation! {:user-id user-id :project-id project-id :convo-id (:id conversation)}})))

(re-frame/reg-event-fx
  ::load-conversation
  event-interceptors
  (fn [{db :db} [_ conversation-id]]
    (let [project-id (subs/get-current-project-id db)]
      {::load-conversation! {:project-id project-id
                             :conversation-id conversation-id
                             :on-success [::conversation-loaded]
                             :on-not-found [::conversation-not-found]}})))

(re-frame/reg-event-fx
  ::conversation-loaded
  event-interceptors
  (fn [{db :db} [_ conversation]]
    (let [user-id (:id (subs/get-current-user db))
          project-id (subs/get-current-project-id db)
          messages (mapv server->message (:messages conversation))
          max-pos (or (some->> messages (seq) (map :position) (apply max)) -1)]
      {:db (update db :project-assistant merge
                   {:conversation (select-keys conversation [:id :title :model :created_at :updated_at])
                    :messages messages
                    :pending-actions-buffer []
                    :next-position (inc max-pos)
                    :streaming? false
                    :error nil})
       ::set-active-conversation! {:user-id user-id :project-id project-id :convo-id (:id conversation)}})))

(re-frame/reg-event-fx
  ::conversation-not-found
  event-interceptors
  (fn [{db :db} _]
    (let [user-id (:id (subs/get-current-user db))
          project-id (subs/get-current-project-id db)]
      {:db (update db :project-assistant merge {:messages [] :conversation nil :next-position 0})
       ::set-active-conversation! {:user-id user-id :project-id project-id :convo-id nil}})))

(re-frame/reg-event-fx
  ::reactivate-conversation
  event-interceptors
  (fn [{db :db} [_ conversation-id]]
    (when-not (get-in db [:project-assistant :streaming?])
      {:db (assoc-in db [:project-assistant :active-tab] :chat)
       :dispatch [::load-conversation conversation-id]})))

;; -- history list browse --

(re-frame/reg-event-fx
  ::switch-tab
  event-interceptors
  (fn [{db :db} [_ tab]]
    (let [project-id (subs/get-current-project-id db)]
      (cond-> {:db (assoc-in db [:project-assistant :active-tab] tab)}
        (= tab :history)
        (assoc ::rf-common/http {:url (broutes/get-url "assistant_conversations#index"
                                                       {:p_id project-id})
                                 :on-success [::conversations-loaded]})))))

(re-frame/reg-event-db
  ::conversations-loaded
  event-interceptors
  (fn [db [_ {:keys [data]}]]
    (assoc-in db [:project-assistant :conversations] (vec data))))

(re-frame/reg-event-db
  ::set-history-search
  event-interceptors
  (fn [db [_ query]]
    (assoc-in db [:project-assistant :history-search] query)))

;; -- history list modify (rename / delete / restore) --

(re-frame/reg-event-db
  ::start-rename
  event-interceptors
  (fn [db [_ conversation-id current-title]]
    (assoc-in db [:project-assistant :rename-state]
              {:id conversation-id :draft (or current-title "")})))

(re-frame/reg-event-db
  ::update-rename-draft
  event-interceptors
  (fn [db [_ draft]]
    (assoc-in db [:project-assistant :rename-state :draft] draft)))

(re-frame/reg-event-db
  ::cancel-rename
  event-interceptors
  (fn [db _]
    (assoc-in db [:project-assistant :rename-state] nil)))

(re-frame/reg-event-fx
  ::commit-rename
  event-interceptors
  (fn [{db :db} _]
    (let [{:keys [id draft]} (get-in db [:project-assistant :rename-state])
          trimmed (str/trim (or draft ""))
          project-id (subs/get-current-project-id db)
          active-id (get-in db [:project-assistant :conversation :id])]
      (if (str/blank? trimmed)
        {:db (assoc-in db [:project-assistant :rename-state] nil)}
        (cond-> {:db (-> db
                         (assoc-in [:project-assistant :rename-state] nil)
                         (update-in [:project-assistant :conversations]
                                    (fn [cs]
                                      (mapv (fn [c]
                                              (if (= (:id c) id)
                                                (assoc c :title trimmed)
                                                c))
                                            cs))))
                 ::rf-common/http {:url (broutes/get-url "assistant_conversations#update"
                                                         {:p_id project-id :id id})
                                   :method "PATCH"
                                   :body {:title trimmed}}}
          (= id active-id)
          (assoc-in [:db :project-assistant :conversation :title] trimmed))))))

(re-frame/reg-event-fx
  ::delete-conversation
  event-interceptors
  (fn [{db :db} [_ conversation-id]]
    (let [project-id (subs/get-current-project-id db)
          active-id (get-in db [:project-assistant :conversation :id])
          user-id (:id (subs/get-current-user db))
          conv (first (filter #(= (:id %) conversation-id)
                              (get-in db [:project-assistant :conversations])))]
      (cond-> {:db (update-in db [:project-assistant :conversations]
                              (fn [cs]
                                (vec (remove #(= (:id %) conversation-id) cs))))
               ::rf-common/http {:url (broutes/get-url "assistant_conversations#destroy"
                                                       {:p_id project-id :id conversation-id})
                                 :method "DELETE"
                                 :on-success [::conversation-deleted conv]}}
        (= conversation-id active-id)
        (->
          ;; TODO: we're resetting active conversation in several places already, add a helper fn
          (update :db #(update % :project-assistant merge
                               {:messages []
                                :pending-actions-buffer []
                                :streaming? false
                                :error nil
                                :conversation nil
                                :next-position 0}))
          (assoc ::set-active-conversation! {:user-id user-id
                                             :project-id project-id
                                             :convo-id nil}))))))

(re-frame/reg-event-fx
  ::conversation-deleted
  event-interceptors
  (fn [_ [_ deleted-conv _deleted-id]]
    (let [toast-id (str "assistant-conv-delete-" (:id deleted-conv))]
      {::notifications/show2
       {:id toast-id
        :message (reagent/as-element
                   [:<>
                    (get-translation "assistant.history.deleted" "Conversation deleted.")
                    " "
                    [:a.assistant-undo-link
                     {:href "#"
                      :on-click (fn [e]
                                  (.preventDefault e)
                                  (notifications/dismiss toast-id)
                                  (re-frame/dispatch [::restore-conversation (:id deleted-conv)]))}
                     (get-translation "assistant.history.undo" "Undo")]])
        :type "info"
        :auto-close 5000}})))

(re-frame/reg-event-fx
  ::restore-conversation
  event-interceptors
  (fn [{db :db} [_ conversation-id]]
    (let [project-id (subs/get-current-project-id db)]
      {::rf-common/http {:url (broutes/get-url "assistant_conversations#restore"
                                               {:p_id project-id :id conversation-id})
                         :method "POST"
                         :on-success [::conversation-restored]}})))

(re-frame/reg-event-db
  ::conversation-restored
  event-interceptors
  (fn [db [_ conv]]
    (update-in db [:project-assistant :conversations]
               (fn [cs]
                 (conj (vec (remove #(= (:id %) (:id conv)) (or cs []))) conv)))))
